import { fetchEvmData } from './evm';
import { fetchSolanaData } from './solana';
import { fetchBitcoinData } from './bitcoin';
import { fetchXrpData } from './xrp';
import { WalletData } from '../../types/crypto';

const globalBalanceCache: Record<string, any> = {};

export const fetchWalletData = async (wallet: any): Promise<WalletData> => {
  try {
    let data;
    switch (wallet.network) {
      case 'evm':
        const ethData = await fetchEvmData(wallet.address, 'ethereum').then(res => {
          globalBalanceCache['ethereum'] = JSON.parse(JSON.stringify(res));
          return JSON.parse(JSON.stringify(res));
        }).catch(e => {
          console.error('ETH fetch failed', e);
          return globalBalanceCache['ethereum'] ? JSON.parse(JSON.stringify(globalBalanceCache['ethereum'])) : { nativeBalance: 0, nativeUsdValue: 0, nativePriceUsd: 0, assets: [] as any[] };
        });
        
        // Only keep allowed tokens for Ethereum (USDC and DAI)
        const allowedEthSymbols = ['USDC', 'DAI'];
        ethData.assets = (ethData.assets || []).filter((asset: any) =>
          allowedEthSymbols.includes((asset.symbol || '').toUpperCase())
        );

        ethData.assets.forEach((asset: any) => {
          if (asset.symbol?.toUpperCase() === 'USDC') asset.name = 'USDC (Ethereum)';
          else if (asset.symbol?.toUpperCase() === 'DAI') asset.name = 'Dai (Ethereum)';
          else if (!asset.name.includes('(Ethereum)')) {
            asset.name = `${asset.name} (Ethereum)`;
          }
        });

        // Ensure default tokens are always visible even if APIs fail
        if (!ethData.assets.find((a: any) => a.symbol?.toUpperCase() === 'USDC')) {
          ethData.assets.push({ id: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48', name: 'USDC (Ethereum)', symbol: 'USDC', balance: 0, usdValue: 0, priceUsd: 1, icon: 'https://assets.coingecko.com/coins/images/6319/small/USDC.png', contractAddress: '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' });
        }
        if (!ethData.assets.find((a: any) => a.symbol?.toUpperCase() === 'DAI')) {
          ethData.assets.push({ id: '0x6b175474e89094c44da98b954eedeac495271d0f', name: 'Dai (Ethereum)', symbol: 'DAI', balance: 0, usdValue: 0, priceUsd: 1, icon: 'https://assets.coingecko.com/coins/images/9956/small/4943.png', contractAddress: '0x6b175474e89094c44da98b954eedeac495271d0f' });
        }
        
        ethData.assets.sort((a: any, b: any) => b.usdValue - a.usdValue);
        data = ethData;
        break;

      case 'polygon':
        const polygonData = await fetchEvmData(wallet.address, 'polygon').then(res => {
          globalBalanceCache['polygon'] = JSON.parse(JSON.stringify(res));
          return JSON.parse(JSON.stringify(res));
        }).catch(e => {
          console.error('POL fetch failed', e);
          return globalBalanceCache['polygon'] ? JSON.parse(JSON.stringify(globalBalanceCache['polygon'])) : { nativeBalance: 0, nativeUsdValue: 0, nativePriceUsd: 0, assets: [] as any[] };
        });

        polygonData.assets.forEach((asset: any) => {
          if (asset.symbol === 'USDC') {
            asset.name = 'USDC';
          }
          if (!asset.name.includes('(Polygon)')) {
            asset.name = `${asset.name} (Polygon)`;
          }
        });

        if (!polygonData.assets.find((a: any) => a.symbol === 'USDC' && a.name.includes('(Polygon)'))) {
          polygonData.assets.push({ id: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', name: 'USDC (Polygon)', symbol: 'USDC', balance: 0, usdValue: 0, priceUsd: 1, icon: 'https://assets.coingecko.com/coins/images/6319/small/USDC.png', contractAddress: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174' });
        }
        
        polygonData.assets.sort((a: any, b: any) => b.usdValue - a.usdValue);
        data = polygonData;
        break;
      case 'solana':
        data = await fetchSolanaData(wallet.address).then(res => { globalBalanceCache['solana'] = JSON.parse(JSON.stringify(res)); return JSON.parse(JSON.stringify(res)); }).catch(e => { return globalBalanceCache['solana'] ? JSON.parse(JSON.stringify(globalBalanceCache['solana'])) : { nativeBalance: 0, nativeUsdValue: 0, nativePriceUsd: 0, assets: [] }; });
        break;
      case 'bitcoin':
        data = await fetchBitcoinData(wallet.address).then(res => { globalBalanceCache['bitcoin'] = JSON.parse(JSON.stringify(res)); return JSON.parse(JSON.stringify(res)); }).catch(e => { return globalBalanceCache['bitcoin'] ? JSON.parse(JSON.stringify(globalBalanceCache['bitcoin'])) : { nativeBalance: 0, nativeUsdValue: 0, nativePriceUsd: 0, assets: [] }; });
        break;
      case 'xrp':
        data = await fetchXrpData(wallet.address).then(res => { globalBalanceCache['xrp'] = JSON.parse(JSON.stringify(res)); return JSON.parse(JSON.stringify(res)); }).catch(e => { return globalBalanceCache['xrp'] ? JSON.parse(JSON.stringify(globalBalanceCache['xrp'])) : { nativeBalance: 0, nativeUsdValue: 0, nativePriceUsd: 0, assets: [] }; });
        break;
      default:
        throw new Error(`Unsupported network: ${wallet.network}`);
    }

    const totalUsdValue = data.nativeUsdValue + data.assets.reduce((sum: number, asset: any) => sum + (asset.usdValue || 0), 0);

    return {
      id: wallet.id,
      name: wallet.name,
      symbol: wallet.symbol,
      network: wallet.network,
      address: wallet.address,
      nativeBalance: data.nativeBalance,
      nativeUsdValue: data.nativeUsdValue,
      nativePriceUsd: data.nativePriceUsd,
      assets: data.assets,
      totalUsdValue,
      lastUpdated: new Date().toISOString(),
      isError: false,
      isLoading: false
    };
  } catch (error) {
    console.error(`Failed to fetch wallet ${wallet.id}:`, error);
    // Return empty 0-balance state instead of throwing so UI never breaks
    return {
      id: wallet.id,
      name: wallet.name,
      symbol: wallet.symbol,
      network: wallet.network,
      address: wallet.address,
      nativeBalance: 0,
      nativeUsdValue: 0,
      nativePriceUsd: 0,
      assets: [],
      totalUsdValue: 0,
      lastUpdated: new Date().toISOString(),
      isError: false,
      isLoading: false
    };
  }
};
